// Print / paper skills for AI edit and deterministic print CSS.
// CSS px at 96dpi (1mm ≈ 3.7795px).
const mm = (n) => Math.round((n * 96) / 25.4);

export const PAPER_SKILLS = [
  {
    id: "a5-portrait",
    widthMm: 148,
    heightMm: 210,
    widthPx: mm(148),
    heightPx: mm(210),
    cssPage: "A5 portrait",
  },
  {
    id: "a5-landscape",
    widthMm: 210,
    heightMm: 148,
    widthPx: mm(210),
    heightPx: mm(148),
    cssPage: "A5 landscape",
  },
  {
    id: "a4-portrait",
    widthMm: 210,
    heightMm: 297,
    widthPx: mm(210),
    heightPx: mm(297),
    cssPage: "A4 portrait",
  },
  {
    id: "a4-landscape",
    widthMm: 297,
    heightMm: 210,
    widthPx: mm(297),
    heightPx: mm(210),
    cssPage: "A4 landscape",
  },
  {
    id: "a3-portrait",
    widthMm: 297,
    heightMm: 420,
    widthPx: mm(297),
    heightPx: mm(420),
    cssPage: "A3 portrait",
  },
  {
    id: "a3-landscape",
    widthMm: 420,
    heightMm: 297,
    widthPx: mm(420),
    heightPx: mm(297),
    cssPage: "A3 landscape",
  },
  {
    id: "letter-portrait",
    widthMm: 215.9,
    heightMm: 279.4,
    widthPx: mm(215.9),
    heightPx: mm(279.4),
    cssPage: "letter portrait",
  },
  {
    id: "letter-landscape",
    widthMm: 279.4,
    heightMm: 215.9,
    widthPx: mm(279.4),
    heightPx: mm(215.9),
    cssPage: "letter landscape",
  },
];

const STYLE_MARK = "frontplay-paper-skill";
const PRINT_COLOR_MARK = "frontplay-print-color-adjust";
const PRINT_RESET_MARK = "frontplay-print-reset";
const PRINT_BEFORE_PRINT_MARK = "frontplay-print-beforeprint";
const CHARSET_META = '<meta charset="UTF-8">';

// Always-injected stylesheet so browsers keep user backgrounds/graphics on print.
// `print-color-adjust` is inherited, but we also set it on `*`/`*::before`/`*::after`
// for maximum cross-browser coverage (Chrome/Edge/Safari "Background graphics" off).
function printColorAdjustBlock() {
  return `<style id="${PRINT_COLOR_MARK}">
/* FrontPlay — force backgrounds/graphics to print (print-color-adjust: exact) */
*, *::before, *::after {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
  </style>`;
}

// Always-injected reset so scroll/fixed layouts print ALL content (no clipping
// into a single viewport). Print-only; does not affect the on-screen preview.
function printLayoutResetBlock() {
  return `<style id="${PRINT_RESET_MARK}">
/* FrontPlay — make scroll/fixed layouts print every page of content */
@media print {
  html, body { height: auto !important; overflow: visible !important; }
  body * { overflow: visible !important; max-height: none !important; }
}
</style>`;
}

// Print-only script: on beforeprint, force hidden (e.g. scroll-reveal) elements
// visible, hide position:fixed overlay chrome, and add page breaks to
// slide-like (full-height) sections. Does not touch the live preview.
function printBeforePrintScript() {
  return `<script id="${PRINT_BEFORE_PRINT_MARK}">
(function(){
  function fpPrepare(){
    var all = document.querySelectorAll('*');
    for (var i=0;i<all.length;i++){
      var el = all[i];
      var cs = getComputedStyle(el);
      if (cs.opacity !== '1' || cs.visibility === 'hidden') {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
        el.style.transform = 'none';
      }
      if (cs.position === 'fixed') { el.style.display = 'none'; }
    }
    var blocks = document.querySelectorAll('section, article');
    for (var j=0;j<blocks.length;j++){
      var b = blocks[j];
      if (b.offsetHeight >= window.innerHeight * 0.8) { b.style.pageBreakAfter = 'always'; }
    }
  }
  if (window.matchMedia) { window.addEventListener('beforeprint', fpPrepare); }
})();
</script>`;
}

export function getPaperSkill(id) {
  if (!id || id === "none") return null;
  return PAPER_SKILLS.find((p) => p.id === id) || null;
}

export function buildPaperSkillPrompt(skill) {
  if (!skill) return "";
  const orientation = skill.widthMm >= skill.heightMm ? "landscape" : "portrait";
  const ratio = (skill.widthPx / skill.heightPx).toFixed(3);
  return `Active skill: paper layout — match the ${orientation} aspect ratio of ${skill.cssPage}.
Target aspect: ${skill.widthMm}mm × ${skill.heightMm}mm (${skill.widthPx} × ${skill.heightPx} CSS px at 96dpi), ratio ≈ ${ratio}.
Treat this as a fixed-aspect print page, NOT a responsive device viewport. The final paper size is chosen by the user in the browser print dialog, so do NOT depend on a fixed physical size — shape the layout to the page's aspect ratio / orientation instead:
- Prefer a page-like root/container whose width:height matches the ${orientation} ratio (avoid opposite-orientation or near-square layouts unless the user asks).
- For <canvas>, set width/height attributes (and redraw) to the same aspect ratio.
- Avoid fluid full-viewport (100vw/100vh) layouts unless the user explicitly asks for screen UI.
- Preserve the user's content intent; only change sizing/layout as needed to fit the orientation.
- Always keep UTF-8; never mojibake or re-encode Japanese/non-ASCII text.`;
}

/** Ensure <meta charset="UTF-8"> exists as early as possible in <head>. */
export function ensureCharset(html) {
  if (typeof html !== "string" || !html) return html;
  if (/<meta\s+charset\s*=/i.test(html) || /<meta\s+http-equiv\s*=\s*["']?content-type/i.test(html)) {
    return html;
  }
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (m) => `${m}\n  ${CHARSET_META}`);
  }
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html[^>]*>/i, (m) => `${m}\n<head>\n  ${CHARSET_META}\n</head>`);
  }
  if (/<!DOCTYPE/i.test(html)) {
    return html.replace(/<!DOCTYPE[^>]*>/i, (m) => `${m}\n<html>\n<head>\n  ${CHARSET_META}\n</head>\n<body>`) + "\n</body>\n</html>";
  }
  return `<!DOCTYPE html>\n<html>\n<head>\n  ${CHARSET_META}\n</head>\n<body>\n${html}\n</body>\n</html>`;
}

function paperStyleBlock(skill) {
  const w = skill.widthMm;
  const h = skill.heightMm;
  return `<style id="${STYLE_MARK}" data-paper="${skill.id}">
/* FrontPlay paper skill — deterministic print layout for download / browser print */
@page { size: ${skill.cssPage}; margin: 10mm; }
@media screen {
  html, body { margin: 0; padding: 0; }
  body {
    box-sizing: border-box;
    width: ${w}mm;
    min-height: ${h}mm;
  }
}
@media print {
  html, body {
    width: auto;
    min-height: auto;
  }
  /* Keep the page margin area white, but let the body keep its own background. */
  html {
    background: transparent !important;
  }
}
</style>`;
}

/**
 * Inject or replace the FrontPlay paper @page stylesheet.
 * Does nothing if skill is null (removes prior injected block).
 */
export function ensurePaperPrintCss(html, skill) {
  let out = ensureCharset(typeof html === "string" ? html : "");
  // Strip previous injections (paper skill + print-color-adjust blocks).
  out = out.replace(
    new RegExp(`<style\\s+id=["']${STYLE_MARK}["'][\\s\\S]*?<\\/style>`, "i"),
    ""
  );
  out = out.replace(
    new RegExp(`<style\\s+id=["']${PRINT_COLOR_MARK}["'][\\s\\S]*?<\\/style>`, "i"),
    ""
  );
  out = out.replace(
    new RegExp(`<style\\s+id=["']${PRINT_RESET_MARK}["'][\\s\\S]*?<\\/style>`, "i"),
    ""
  );
  out = out.replace(
    new RegExp(`<script\\s+id=["']${PRINT_BEFORE_PRINT_MARK}["'][\\s\\S]*?<\\/script>`, "i"),
    ""
  );

  // Always inject the print-color-adjust block so backgrounds survive printing,
  // regardless of whether a paper skill is selected.
  const paperBlock = skill ? paperStyleBlock(skill) : "";
  const colorBlock = printColorAdjustBlock();
  const resetBlock = printLayoutResetBlock();
  const beforeScript = printBeforePrintScript();
  const headInner = `  ${colorBlock}\n  ${resetBlock}\n  ${beforeScript}${paperBlock ? "\n  " + paperBlock : ""}`;

  if (/<head[^>]*>/i.test(out)) {
    return out.replace(/<head[^>]*>/i, (m) => `${m}\n${headInner}`);
  }
  if (/<html[^>]*>/i.test(out)) {
    return out.replace(
      /<html[^>]*>/i,
      (m) => `${m}\n<head>\n  ${CHARSET_META}\n${headInner}\n</head>`
    );
  }
  return `<!DOCTYPE html>\n<html>\n<head>\n  ${CHARSET_META}\n${headInner}\n</head>\n<body>\n${out}\n</body>\n</html>`;
}

/** Build standalone HTML ready to open/print in a browser (download / print flow). */
export function buildStandaloneHtml(code, paperSkillId) {
  const skill = getPaperSkill(paperSkillId);
  return ensurePaperPrintCss(code, skill);
}
